import React, { createContext, useContext, ReactNode } from 'react';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { WorkplaceModuleRoutes } from '@common-frontend-submodule/enums/routes/workplace-module-routes.enum';
import { IWorkplaceModuleCreationDTO } from '@common-frontend-submodule/interfaces/workplace-module/iworkplace-module-creation.interface';
import { IWorkplaceModuleUpdatePositionDTO } from '@common-frontend-submodule/interfaces/workplace-module/iworkplace-module-update-position.interface';
import { IWorkplaceModuleDTO } from '@common-frontend-submodule/interfaces/workplace-module/iworkplace-module.interface';
import ApiClient from '../../lib/api-client';

interface WorkplaceModuleContextProps {
  getByWorkplaceId: (workplaceId: string) => Promise<IApiResponseGenericDTO<IWorkplaceModuleDTO[]>>;
  create: (model: IWorkplaceModuleCreationDTO) => Promise<IApiResponseGenericDTO<IWorkplaceModuleDTO>>;
  updatePositions: (workplaceId: string, model: IWorkplaceModuleUpdatePositionDTO) => Promise<IApiResponseGenericDTO<boolean>>;
  delete: (workplaceModuleId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const WorkplaceModuleContext = createContext<WorkplaceModuleContextProps | undefined>(undefined);

export const WorkplaceModuleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const apiClient = ApiClient.getInstance()

  const getByWorkplaceId = async (workplaceId: string): Promise<IApiResponseGenericDTO<IWorkplaceModuleDTO[]>> => {
    return await apiClient.getRequest<IWorkplaceModuleDTO[]>(`${WorkplaceModuleRoutes.BasePrefix}/all/${workplaceId}`);
  };

  const create = async (model: IWorkplaceModuleCreationDTO): Promise<IApiResponseGenericDTO<IWorkplaceModuleDTO>> => {
    return await apiClient.postRequest<IWorkplaceModuleCreationDTO, IWorkplaceModuleDTO>(WorkplaceModuleRoutes.BasePrefix, model);
  };

  const updatePositions = async (workplaceId: string, model: IWorkplaceModuleUpdatePositionDTO): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.putRequest<IWorkplaceModuleUpdatePositionDTO, boolean>(`${WorkplaceModuleRoutes.BasePrefix}/${workplaceId}`, model);
  };

  const deleteModule = async (workplaceModuleId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${WorkplaceModuleRoutes.BasePrefix}/${workplaceModuleId}`);
  };

  return (
    <WorkplaceModuleContext.Provider value={{ getByWorkplaceId, create, updatePositions, delete: deleteModule }}>
      {children}
    </WorkplaceModuleContext.Provider>
  );
};

export const useWorkplaceModuleStore = (): WorkplaceModuleContextProps => {
  const context = useContext(WorkplaceModuleContext);
  if (!context) {
    throw new Error('useWorkplaceModuleStore must be used within a WorkplaceModuleProvider');
  }
  return context;
};
